/**
 * Two-step CMS upload (docs/06 §3): ask the API to sign, then PUT the bytes.
 *
 * `POST /cms/uploads/sign` answers either a presigned R2 URL (`mode: 'presigned'`, the bytes go
 * straight to the bucket) or an API path (`mode: 'proxy'`, the Worker relays up to 10 MB). The
 * caller only gets the final R2 key back and stores it on the entity.
 */
import { type UploadSignBodyInput, type UploadSignResponse } from '@akpl/shared';

import { api, API_BASE, getAuthToken } from '../api';

export interface UploadResult {
  key: string;
  mode: UploadSignResponse['mode'];
}

export async function uploadFile(
  file: File,
  request: Omit<UploadSignBodyInput, 'filename' | 'contentType' | 'size'>,
): Promise<UploadResult> {
  const signed = await api.cmsUploadSign({
    body: {
      ...request,
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size,
    },
  });

  const headers: Record<string, string> = { ...signed.headers };
  let url = signed.url;
  if (signed.mode === 'proxy') {
    // Our own endpoint: absolute URL + the operator's token (the presigned URL carries its own).
    url = `${API_BASE}${signed.url}`;
    const token = await getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, { method: 'PUT', headers, body: file });
  if (!response.ok) {
    throw new Error(`upload_failed_${response.status}`);
  }
  return { key: signed.key, mode: signed.mode };
}
